<template>
  <div class="container-custom oh-page" id="order-history-page" :style="styleObject">
    <header class="oh-header">
      <OrderTopBar :show-back="isCompletedOnly" @back="gotoBackProfile" @lang="syncOrderNames" />
      <h1 class="oh-title">{{ $t("orderHistory.title") }}</h1>
    </header>

    <div class="content-custom">
    <main class="oh-main">
      <!-- Empty state -->
      <section v-if="!isLoadingInitial && dataOrders.length === 0" class="oh-empty">
        <div class="oh-empty__icon">
          <span class="material-icons-round">receipt_long</span>
        </div>
        <h2 class="oh-empty__title">{{ $t("orderHistory.emptyTitle") }}</h2>
        <p class="oh-empty__text">{{ $t("orderHistory.emptyText") }}</p>
        <button type="button" class="oh-empty__btn" @click="goToMenu">
          {{ $t("orderHistory.browseMenu") }}
        </button>
      </section>

      <!-- Order list -->
      <div v-else class="oh-list">
        <article
          v-for="(order, idx) in dataOrders"
          :key="`${order.id}-${idx}`"
          class="oh-card"
          :class="{ 'oh-card--static': isCompletedOnly && !canOpenOrder }"
          :role="isCompletedOnly && !canOpenOrder ? null : 'button'"
          :tabindex="isCompletedOnly && !canOpenOrder ? null : 0"
          @click="openOrder(order)"
          @keyup.enter="openOrder(order)"
        >
          <div class="oh-card__top">
            <div class="oh-card__field">
              <template v-for="(ref, refIdx) in orderRefLines(order)" :key="refIdx">
                <span class="oh-card__label" :class="{ 'oh-card__label--sub': !ref.main }">{{ ref.label }}</span>
                <span class="oh-card__value" :class="{ 'oh-card__value--sub': !ref.main }">{{ ref.value }}</span>
              </template>
              <span v-if="order.created_at" class="oh-card__date">{{ dateFormat(order.created_at) }}</span>
            </div>
            <div class="oh-card__field oh-card__field--right">
              <template v-if="!isCompletedOnly && order.table_code">
                <span class="oh-card__label">{{ $t("orderHistory.table") }}</span>
                <span class="oh-card__table-badge">{{ order.table_no }}</span>
              </template>
              <span class="oh-card__label" :class="{ 'oh-card__label--mt': !isCompletedOnly && order.table_code }">{{ $t("orderHistory.status") }}</span>
              <div class="oh-card__status" :class="`oh-card__status--${statusKey(order)}`">
                <span class="oh-card__pulse" :class="`oh-card__pulse--${statusKey(order)}`"></span>
                <span>{{ statusLabel(order) }}</span>
              </div>
            </div>
          </div>

          <div class="oh-card__items">
            <div
              v-for="(item, itemIdx) in orderItems(order)"
              :key="itemIdx"
              class="oh-card__entry"
            >
              <div class="oh-card__row">
                <div class="oh-card__row-left">
                  <span class="oh-card__qty">{{ item.quantity }}x</span>
                  <span class="oh-card__name">{{ tname(item.item_name || item.name) }}</span>
                </div>
                <div class="oh-card__price-stack">
                  <span v-if="formatItemPriceWas(order, item)" class="oh-card__price-was">{{ formatItemPriceWas(order, item) }}</span>
                  <span class="oh-card__price">{{ formatItemPrice(order, item) }}</span>
                </div>
              </div>
              <ul
                v-if="modifierLinesFor(item).length"
                class="oh-card__modifiers"
              >
                <li
                  v-for="(line, lineIdx) in modifierLinesFor(item)"
                  :key="lineIdx"
                  class="oh-card__mod-line"
                  :class="{ 'oh-card__mod-line--header': line.isHeader, 'oh-card__mod-line--nested': line.isNested }"
                >
                  <span class="oh-card__mod-name" :class="{ 'oh-card__mod-name--off': line.unavailable }">{{ line.label }}</span>
                  <span v-if="line.unavailable" class="oh-card__mod-na">{{ $t("orderHistory.notAvailableItem") }}</span>
                  <span v-else-if="line.priceLabel" class="oh-card__mod-price">{{ line.priceLabel }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div
            v-if="orderTotals(order).hasSummary"
            class="oh-card__summary"
          >
            <div
              v-if="orderTotals(order).subtotal > 0"
              class="oh-card__summary-row"
            >
              <span>{{ $t("orderHistory.subtotal") }}</span>
              <span>${{ orderTotals(order).subtotal.toFixed(2) }}</span>
            </div>
            <div
              v-if="orderTotals(order).discount > 0"
              class="oh-card__summary-row oh-card__summary-row--discount"
            >
              <span>
                {{ $t("orderHistory.voucherDiscount") }}{{
                  orderTotals(order).voucherLabel
                    ? ` (${orderTotals(order).voucherLabel})`
                    : ""
                }}
              </span>
              <span>-${{ orderTotals(order).discount.toFixed(2) }}</span>
            </div>
            <div
              v-if="orderTotals(order).serviceCharge > 0"
              class="oh-card__summary-row"
            >
              <span>{{ $t("orderHistory.serviceCharge") }}</span>
              <span>${{ orderTotals(order).serviceCharge.toFixed(2) }}</span>
            </div>
            <div
              v-if="orderTotals(order).gst > 0"
              class="oh-card__summary-row"
            >
              <span>{{ $t("orderHistory.gstPercent", { percent: Math.abs(orderTotals(order).gstRate) }) }}</span>
              <span>${{ orderTotals(order).gst.toFixed(2) }}</span>
            </div>
            <div
              v-if="orderTotals(order).bcrsQty > 0"
              class="oh-card__summary-row oh-card__summary-row--bill-total"
            >
              <span>{{ $t("cart.billTotal") }}</span>
              <span>${{ orderTotals(order).billTotal.toFixed(2) }}</span>
            </div>
            <div
              v-if="orderTotals(order).bcrsQty > 0"
              class="oh-card__summary-row"
            >
              <span>{{ $t("cart.bcrsDeposit") }} ({{ orderTotals(order).bcrsQty }} x ${{ orderTotals(order).eachBcrsAmount.toFixed(2) }})</span>
              <span>${{ orderTotals(order).bcrsAmount.toFixed(2) }}</span>
            </div>
            <div class="oh-card__summary-row oh-card__summary-row--total">
              <span>{{ 0 > orderTotals(order).gstRate ? $t("orderHistory.totalInclGst") : $t("cart.totalPayable") }}</span>
              <span class="oh-card__summary-amount--payable">${{ orderTotals(order).total.toFixed(2) }}</span>
            </div>
          </div>
          <p v-else class="oh-card__disclaimer">{{ $t("orderHistory.disclaimer") }}</p>
        </article>

        <button
          v-if="isCompletedOnly ? totalRealOrder > 5 : canLoadMore"
          type="button"
          class="oh-load-more"
          :disabled="loadingMore"
          @click.stop="loadMore"
        >
          {{ loadingMore ? $t("common.loadingMore") : $t("common.loadMore") }}
        </button>
      </div>
    </main>
    </div>

    <footer>
      <Footer></Footer>
      <VoucherAvailable></VoucherAvailable>
    </footer>

    <div class="oh-snackbar">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="isCompletedOnly ? loaderActive : (loading && dataOrders.length === 0)" />
  </div>
</template>

<script>
import moment from "moment";
import OrderService from "@/services/order.service";
import { localStorageName, ensureMerchantId } from "@/config/utils";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import Footer from "@/common/Footer/OrderFooter";
import VoucherAvailable from "@/common/VoucherAvailable/VoucherAvailable";
import OrderTopBar from "@/components/OrderTopBar.vue";
import {
  translatedName,
  collectItemNames,
  loadMenuTranslations,
} from "@/i18n/menuTranslate";

export default {
  name: "OrderHistory",
  components: {
    Footer,
    VoucherAvailable,
    OrderTopBar,
    MaterialSnackbar,
    MaterialLoading,
  },
  data() {
    return {
      merchantCode: "",
      branchCode: "",
      pathType: "",
      tableCode: "",
      memberInfo: null,
      memberId: "",
      // Per-container BCRS rate from branch config, for the "(n x $0.10)" label.
      eachBcrsAmount: 0,
      dataOrders: [],
      page: 0,
      // Default (OrderHistory) pagination.
      total: 0,
      loading: false,
      loadingMore: false,
      // CompletedOrderHistoryForPayFirst pagination — the "more than 5" flag
      // and full-screen loader the member/non-member/table endpoints use.
      totalRealOrder: 0,
      loaderActive: false,
      typeChecked: 1,
      valueFromParent: "default_value",
      snackbar: null,
      message: "",
    };
  },
  computed: {
    // MTH086: this file also serves the "Completed Orders" screen reached from
    // Profile (route name CompletedOrderHistoryForPayFirst) — same cards, same
    // styling, but its own fetch strategy (member/non-member/table, completed
    // status only) and a back button instead of the bottom-nav header. Every
    // behavioural difference between the two screens is gated on this flag.
    isCompletedOnly() {
      return this.$route.name === "CompletedOrderHistoryForPayFirst";
    },
    isLoadingInitial() {
      return this.isCompletedOnly ? this.loaderActive : this.loading;
    },
    canLoadMore() {
      return !this.loading && !this.loadingMore && this.dataOrders.length < this.total;
    },
    // Table (QR) mode never had a way into the order detail on the Completed
    // Orders screen — the whole card is static there instead of clickable.
    canOpenOrder() {
      return !this.tableCode;
    },
    styleObject() {
      const base =
        this.$store.state.mainThemeColorTheme2 ||
        this.$store.state.color ||
        "#201a1a";
      return {
        "--color": base,
        "--theme-20": this.blendWhite(base, 0.04),
        "--theme-30": this.blendWhite(base, 0.0675),
        "--theme-40": this.blendWhite(base, 0.16),
      };
    },
  },
  async created() {
    const r = this.$route;
    this.merchantCode = this.$store.state.merchantCode || r.params.merchant_code || "";
    this.branchCode = this.$store.state.branchCode || r.params.branch_code || "";
    this.pathType = r.params.type || "";
    this.tableCode = r.params.table_code || "";

    const memberRaw = localStorage.getItem(localStorageName("memberInfo"));
    if (memberRaw) {
      try {
        this.memberInfo = JSON.parse(memberRaw);
        this.memberId = this.memberInfo?.id || "";
      } catch (e) {
        // ignore
      }
    }

    const branchRaw = localStorage.getItem(localStorageName("BRANCH_INFO"));
    if (branchRaw) {
      try {
        const b = JSON.parse(branchRaw);
        this.eachBcrsAmount = Number(b.each_bcrs_amount) || 0;
      } catch (e) {
        // ignore
      }
    }

    await ensureMerchantId(this.branchCode);
    if (this.isCompletedOnly) {
      this.changeTab(1);
    } else {
      this.fetchOrders();
    }
  },
  methods: {
    tname(name) {
      return translatedName(name || "");
    },
    // Blend a #rrggbb theme colour with white at the given ratio, giving an
    // opaque colour equal to `theme @ ratio` composited over white.
    blendWhite(color, ratio) {
      if (typeof color !== "string") return color;
      const s = color.trim();
      let r, g, b, m;
      if ((m = s.match(/^#([0-9a-fA-F]{6})$/))) {
        r = parseInt(m[1].slice(0, 2), 16);
        g = parseInt(m[1].slice(2, 4), 16);
        b = parseInt(m[1].slice(4, 6), 16);
      } else if ((m = s.match(/^#([0-9a-fA-F]{3})$/))) {
        r = parseInt(m[1][0] + m[1][0], 16);
        g = parseInt(m[1][1] + m[1][1], 16);
        b = parseInt(m[1][2] + m[1][2], 16);
      } else if ((m = s.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i))) {
        r = +m[1];
        g = +m[2];
        b = +m[3];
      } else {
        return color;
      }
      const mix = (c) => Math.round(ratio * c + (1 - ratio) * 255);
      const h = (v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0");
      return "#" + h(mix(r)) + h(mix(g)) + h(mix(b));
    },
    // Header language pill: re-translate item names across loaded orders
    // into the active locale (cached server-side, so it's cheap).
    syncOrderNames() {
      loadMenuTranslations(collectItemNames(this.dataOrders), this.$i18n.locale);
    },
    loadMore() {
      if (this.isCompletedOnly) {
        if (this.memberInfo) {
          this.getOrdersByMember(this.typeChecked);
        } else {
          this.getOrdersByNonMember(this.typeChecked);
        }
        return;
      }
      this.page += 1;
      this.fetchOrders({ append: true });
    },
    fetchOrders({ append = false } = {}) {
      if (append) {
        this.loadingMore = true;
      } else {
        this.loading = true;
        this.page = 0;
        this.dataOrders = [];
        this.total = 0;
      }

      const payload = {
        branch_code: this.branchCode,
        table_code: this.tableCode || "",
        member_id: this.memberId || 0,
        page: this.page,
      };

      OrderService.getOrdersHistory(payload)
        .then((res) => {
          if (res && res.data && res.data.result && res.data.result.isSuccess) {
            const d = res.data.data || {};
            const items = d.data || [];
            this.total = d.total ?? 0;
            this.dataOrders = append ? this.dataOrders.concat(items) : items;
            this.syncOrderNames();
          } else {
            if (!append) this.dataOrders = [];
            this.snackbar = "danger";
            this.message =
              (res.data && res.data.result && res.data.result.message) ||
              this.$t("orderHistory.unableToLoad");
            this.setTimeOutNotification();
          }
        })
        .catch((error) => {
          if (!append) this.dataOrders = [];
          this.snackbar = "danger";
          this.message = (error && error.message) || this.$t("orderHistory.unableToLoad");
          this.setTimeOutNotification();
        })
        .finally(() => {
          this.loading = false;
          this.loadingMore = false;
        });
    },
    // Identifier block for the Completed Orders screen, mirroring the default
    // screen's Receipt No / Txn ID stack but with its own primary reference:
    // the order number while the order is still ongoing, the receipt number
    // once it is completed. The default screen just shows receipt_no and
    // transaction_id side by side, both secondary-styled.
    orderRefLines(order) {
      const lines = [];
      if (this.isCompletedOnly) {
        if (this.typeChecked == 2) {
          lines.push({
            label: this.$t("orderHistory.receiptNo"),
            value: `#${order.receipt_no}`,
            main: true,
          });
        } else {
          lines.push({
            label: this.$t("orderHistory.orderLabel"),
            value: `#${this.merchantCode}-${order.order_number}`,
            main: true,
          });
          if (order.receipt_no) {
            lines.push({ label: this.$t("orderHistory.receiptNo"), value: order.receipt_no });
          }
        }
        if (order.transaction_id) {
          lines.push({ label: this.$t("orderHistory.transactionId"), value: order.transaction_id });
        }
        return lines;
      }

      if (order.receipt_no) {
        lines.push({ label: this.$t("orderHistory.receiptNo"), value: order.receipt_no });
      }
      if (order.transaction_id) {
        lines.push({ label: this.$t("orderHistory.transactionId"), value: order.transaction_id });
      }
      return lines;
    },
    // POS push state for a non-table (member/non-member) order on the
    // Completed Orders screen: null/not yet pushed = still confirming, 1 =
    // kitchen is preparing it, 2 = ready.
    pushStatusKey(order) {
      if (order.is_pushed_to_megapos_database == 1) return "preparing";
      if (order.is_pushed_to_megapos_database == 2) return "ready";
      return "confirming";
    },
    // Completed orders get the green "done" chip; each of the ongoing states
    // the old markup showed gets its own colour too, mirroring
    // statusLabel()'s exact branching so the key and the label always match:
    // table (QR) orders are "Seated", otherwise pushStatusKey() decides
    // Confirming / Preparing / Ready. The default screen instead reflects the
    // order's own numeric status (paid / unpaid / voided) straight from the API.
    statusKey(order) {
      if (this.isCompletedOnly) {
        if (this.typeChecked == 2) return "completed";
        if (this.tableCode) return "seated";
        return this.pushStatusKey(order);
      }
      const n = Number(order.status);
      if (n === 1) return "paid";
      if (n === 0) return "unpaid";
      if (n === -1) return "voided";
      return "unpaid";
    },
    statusLabel(order) {
      if (this.isCompletedOnly) {
        if (this.typeChecked == 2) return this.$t("orderHistory.statusCompleted");
        if (this.tableCode) return this.$t("orderHistory.statusSeated");
        const key = this.pushStatusKey(order);
        if (key === "preparing") return this.$t("orderHistory.statusPreparing");
        if (key === "ready") return this.$t("orderHistory.statusReady");
        return this.$t("orderHistory.statusConfirming");
      }
      const key = this.statusKey(order);
      if (key === "paid") return this.$t("orderHistory.statusPaid");
      if (key === "unpaid") return this.$t("orderHistory.statusUnpaid");
      if (key === "voided") return this.$t("orderHistory.statusVoided");
      return "—";
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
    goToMenu() {
      this.$router
        .push({
          name: "Order",
          params: {
            merchant_code: this.merchantCode,
            type: this.pathType,
            branch_code: this.branchCode,
            table_code: this.tableCode,
          },
        })
        .catch(() => {});
    },
    openOrder(order) {
      if (this.isCompletedOnly && !this.canOpenOrder) return;
      if (!order || !order.transaction_id) return;
      const routeConfig = {
        name: "PaymentSuccess",
        params: {
          merchant_code: this.merchantCode,
          type: this.pathType,
          branch_code: this.branchCode,
          table_code: this.tableCode,
          order_id: order.transaction_id,
        },
      };
      // PaymentSuccess doubles as the read-only order-detail screen. Flag the
      // source so leaving it does not wipe an in-progress cart (MTH063).
      // Both screens this component serves are read-only views, so the flag is
      // unconditional — gating it on isCompletedOnly left the plain Order
      // History path unflagged and PaymentSuccess.goBack() cleared the cart.
      routeConfig.query = { from: "order-history" };
      this.$router.push(routeConfig).catch(() => {});
    },
    gotoBackProfile() {
      this.$router.push("profile");
    },
    goBack() {
      this.$router.go(-1);
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
    changeTab(type) {
      this.dataOrders = [];
      if (type == 2) {
        this.valueFromParent = Math.random();
        setTimeout(() => {
          this.typeChecked = type;
        }, 500);
      } else {
        this.typeChecked = type;
      }

      if (this.tableCode) {
        this.getOrdersByTable(type);
      } else {
        if (this.memberInfo) {
          this.getOrdersByMember(type);
        } else {
          this.getOrdersByNonMember(type);
        }
      }
    },
    getOrdersByMember(type) {
      this.showLoader();
      const currentPage = Math.ceil(this.dataOrders.length / (this.page === 0 ? 5 : 3));
      const perPage = this.page === 0 ? 5 : 3;

      OrderService.getOrdersByMember(this.branchCode, type == 1 ? "PAID" : "ACCEPTED", currentPage, perPage).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.page = response.data.data.page;
            this.dataOrders = this.page === 0 ? response.data.data.data : this.dataOrders.concat(response.data.data.data);
            if(response.data.data.data && response.data.data.data.length < 3) {
              this.totalRealOrder = 0;
            } else {
              this.totalRealOrder = response.data.data.total;
            }
            this.syncOrderNames();
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error;
          this.hideLoader();
        }
      );
    },
    getOrdersByNonMember(type) {
      this.showLoader();
      const ordersId = JSON.parse(localStorage.getItem(localStorageName('ORDER_ID_NON_MEMBER'), []));
      const currentPage = Math.ceil(this.dataOrders.length / (this.page === 0 ? 5 : 3));

      var dataForm = {
        branch_code: this.branchCode,
        status: type == 1 ? "PAID" : "ACCEPTED",
        orders_id: ordersId,
        current_page: currentPage
      }

      OrderService.getOrdersByNonMember(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.page = response.data.data.page;
            this.dataOrders = this.page === 0 ? response.data.data.data : this.dataOrders.concat(response.data.data.data);
            if(response.data.data.data && response.data.data.data.length < 3) {
              this.totalRealOrder = 0;
            } else {
              this.totalRealOrder = response.data.data.total;
            }
            this.syncOrderNames();
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error;
          this.hideLoader();
        }
      );
    },
    getOrdersByTable(type) {
      this.showLoader();
      var dataForm = {
        branch_code: this.branchCode,
        table_code: this.tableCode,
        status: type == 1 ? "PAID" : "ACCEPTED",
        member_id: this.memberInfo ? this.memberInfo.id : 0
      }

      OrderService.getOrdersByTable(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.dataOrders = response.data.data;
            this.totalRealOrder = 0;
            this.syncOrderNames();
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = error;
          this.hideLoader();
        }
      );
    },
    orderItems(order) {
      const raw = order.items_ordering_data;
      if (!raw) return [];
      let parsed;
      if (Array.isArray(raw)) {
        parsed = raw;
      } else if (typeof raw === "string") {
        try {
          parsed = JSON.parse(raw);
          if (!Array.isArray(parsed)) return [];
        } catch (e) {
          return [];
        }
      } else {
        return [];
      }

      // Some rows store a flat list linked by parent_item_id instead of nested
      // modifiers — normalise into the nested shape by attaching modifiers to
      // their parent before rendering.
      const hasParentField = parsed.some((item) => item.parent_item_id !== undefined);
      if (!hasParentField) return parsed;

      const mainItems = [];
      const byItemId = new Map();
      for (const item of parsed) {
        const pid = Number(item.parent_item_id ?? -1);
        if (pid <= 0) {
          const normalized = { ...item, modifiers: [] };
          mainItems.push(normalized);
          byItemId.set(item.item_id, normalized);
        } else {
          const parent = byItemId.get(pid);
          if (parent) {
            parent.modifiers.push({
              modifier_name: item.item_name,
              price: item.item_price ?? 0,
              price_default: item.item_price ?? 0,
              quantity: item.quantity ?? 1,
            });
          }
        }
      }
      return mainItems;
    },
    // Whether member pricing applies to this row, for the Completed Orders
    // screen: in table (QR) mode the item's own is_member flag decides,
    // otherwise the order-level isNonMember flag set by the API does. The
    // default screen has no such order-level flag — it just reads the item's
    // own is_member directly.
    isMemberPricing(order, item) {
      if (this.tableCode) return !!item.is_member;
      return order.isNonMember === false;
    },
    // The item's own unit price, excluding whatever its selected modifiers
    // add — falls back to the combined price_default for older/flat-format
    // orders that don't carry the *_no_modifier_option fields.
    itemOwnUnitPrice(order, item) {
      const member = this.isCompletedOnly ? this.isMemberPricing(order, item) : !!item.is_member;
      const ownPrice = member && item.member_price_no_modifier_option
        ? item.member_price_no_modifier_option
        : item.price_default_no_modifier_option;
      if (ownPrice != null && ownPrice !== "") return Number(ownPrice);
      return member && item.member_price
        ? Number(item.member_price)
        : Number(item.price_default ?? item.item_price ?? item.price ?? 0);
    },
    formatItemPrice(order, item) {
      const unit = this.itemOwnUnitPrice(order, item);
      const qty = Number(item.quantity || 1);
      return `$${(unit * qty).toFixed(2)}`;
    },
    // Pre-discount item-only price, struck through next to formatItemPrice()
    // when the member price differs from the list price.
    formatItemPriceWas(order, item) {
      const member = this.isCompletedOnly ? this.isMemberPricing(order, item) : !!item.is_member;
      if (
        member &&
        item.member_price_no_modifier_option &&
        item.price_default_no_modifier_option != item.member_price_no_modifier_option
      ) {
        const qty = Number(item.quantity || 1);
        return `$${(Number(item.price_default_no_modifier_option) * qty).toFixed(2)}`;
      }
      return "";
    },
    modUnitPrice(mod) {
      const hasMember = this.isCompletedOnly ? !!this.memberInfo : !!this.memberId;
      if (hasMember) {
        const member = Number(mod.member_price);
        if (Number.isFinite(member) && member > 0) return member;
      }
      const priceDefault = Number(mod.price_default);
      if (Number.isFinite(priceDefault)) return priceDefault;
      return Number(mod.price || 0);
    },
    buildModLine(label, mod, subQty, itemQty, isNested = false) {
      const modQty = Number(mod.quantity || 1);
      const unit = this.modUnitPrice(mod);
      const total = unit * modQty * subQty * itemQty;
      return {
        // priceLabel is the total for all modQty of them, so the count has to
        // be on the line — "Sour Plum Drink +$9.00" reads as a $9 modifier.
        label: modQty > 1 ? `${modQty}x ${label}` : label,
        priceLabel: total > 0 ? `+$${total.toFixed(2)}` : "",
        // Sold-out modifier, flagged by the API — shown greyed with a
        // "Not Available" note on the Completed Orders screen only.
        unavailable: this.isCompletedOnly && mod.isCanGet == 1,
        isHeader: false,
        isNested,
      };
    },
    // Selected item_in_mog sub-modifiers, nested under a top-level modifier
    // (e.g. "Add-Ons" under "Chicken Tortilla Wrap") — appended as indented
    // lines right after their parent.
    pushItemInMogLines(lines, mod, subQty, itemQty) {
      if (!mod || !mod.item_in_mog || !Array.isArray(mod.item_in_mog.modifier_group)) return;
      mod.item_in_mog.modifier_group.forEach((subGroup) => {
        (subGroup.items || []).forEach((subMod) => {
          if (!subMod || subMod.selected === false) return;
          const label = subMod.item_name || subMod.name;
          if (!label) return;
          lines.push(this.buildModLine(this.tname(label), subMod, subQty, itemQty, true));
        });
      });
    },
    modifierLinesFor(item) {
      const lines = [];
      const itemQty = Number(item.quantity || 1);

      if (Array.isArray(item.modifier_group)) {
        item.modifier_group.forEach((group) => {
          (group.items || []).forEach((mod) => {
            if (!mod || mod.selected === false) return;
            const label = mod.item_name || mod.name;
            if (!label) return;
            lines.push(this.buildModLine(this.tname(label), mod, 1, itemQty));
            this.pushItemInMogLines(lines, mod, 1, itemQty);
          });
        });
      }

      const cs = item.choice_selected;
      // The Completed Orders screen only renders choice_selected lines when
      // the item itself is flagged as a choice item; the default screen has
      // no such guard.
      const choiceGuardPassed = this.isCompletedOnly ? !!item.choice : true;
      if (choiceGuardPassed && cs && Array.isArray(cs.items)) {
        cs.items.forEach((ci) => {
          const detail = ci && ci.item_detail;
          if (!detail) return;
          const subQty = Number(ci.qty || 1);
          if (detail.item_name) {
            lines.push({
              label: `${subQty} × ${this.tname(detail.item_name)}`,
              priceLabel: "",
              isHeader: true,
            });
          }
          (detail.modifier_group || []).forEach((group) => {
            (group.items || []).forEach((mod) => {
              if (!mod || mod.selected === false) return;
              const label = mod.item_name || mod.name;
              if (!label) return;
              lines.push(this.buildModLine(this.tname(label), mod, subQty, itemQty));
              this.pushItemInMogLines(lines, mod, subQty, itemQty);
            });
          });
        });
      }

      if (Array.isArray(item.modifiers)) {
        item.modifiers.forEach((m) => {
          const label = m.modifier_name || m.name;
          if (!label) return;
          const unit = Number(m.price != null ? m.price : m.price_default || 0);
          const modQty = Number(m.quantity || 1);
          const total = unit * modQty * itemQty;
          lines.push({
            label: this.tname(label),
            priceLabel: total > 0 ? `+$${total.toFixed(2)}` : "",
            isHeader: false,
          });
        });
      }

      return lines;
    },
    // GST on the merged base. `gstRatePct` is the stored percent (negative =
    // inclusive).
    computeGst(netSubtotal, serviceCharge, gstRatePct) {
      const rate = Math.abs(Number(gstRatePct) || 0) / 100;
      if (!rate) return 0;
      const base = Math.max(0, netSubtotal) + serviceCharge;
      const raw = gstRatePct < 0 ? base - base / (1 + rate) : base * rate; // inclusive/exclusive
      return Math.round((raw + Number.EPSILON) * 100) / 100;
    },
    orderTotals(order) {
      const num = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };
      const subtotal = num(order.total_order);
      // MTH102: a stored percentage discount can carry a third decimal (5% of
      // $3.50 = 0.175). Round half-up to the cent here, not at `.toFixed(2)`
      // in the template — that one rounds a .5 cent down to $0.17.
      const discount = Math.round((num(order.voucher_amount) + Number.EPSILON) * 100) / 100;
      const serviceCharge = num(order.service_charge_amount);
      const gstRate = num(order.gst);
      const gst = this.computeGst(subtotal - discount, serviceCharge, gstRate);
      // Inclusive GST (gstRate < 0) is already embedded in subtotal/serviceCharge —
      // `gst` here is only the extracted portion for display, not an extra charge.
      const billTotal = Math.max(0, subtotal - discount + serviceCharge + (gstRate < 0 ? 0 : gst));
      const bcrsAmount = num(order.bcrs_total_amount);
      const bcrsQty = num(order.bcrs_item_count);
      const total = Math.max(0, billTotal + bcrsAmount);
      return {
        subtotal,
        discount,
        serviceCharge,
        gstRate,
        gst,
        billTotal,
        bcrsQty,
        bcrsAmount,
        eachBcrsAmount: this.eachBcrsAmount,
        total,
        voucherLabel: order.voucher_label || order.discount_label || "",
        hasSummary: total > 0 || subtotal > 0,
      };
    },
    dateFormat(date) {
      if (!date) return "";
      return moment(date).format("YYYY-MM-DD HH:mm");
    },
  },
};
</script>

<style scoped>
.oh-page {
  --pf: var(--color);
  --pf-muted: color-mix(in srgb, var(--color) 50%, #ffffff);
  --rw: var(--color);
  --rw-18: color-mix(in srgb, var(--color) 18%, #ffffff);
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 480px;
  margin: 0 auto;
  position: relative;
}

.oh-header {
  background-color: var(--theme-30, white);
}

.content-custom {
  flex: 1;
  background-color: var(--theme-30, white);
  overflow: auto;
}

/* material-dashboard forces Roboto Slab on every h1/h2/h3 — opt back into the
   page's own stack. */
.oh-title {
  margin: 0.75rem 0 0.25rem;
  padding: 0 1rem;
  font-family: inherit;
  font-weight: 800;
  font-size: 1.5rem;
  color: #1a1c1a;
}

.oh-main {
  padding: 0.5rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.oh-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.oh-card {
  padding: 1.5rem;
  background: #fff;
  border-radius: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 14px rgba(25, 28, 30, 0.04);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}

.oh-card:active {
  transform: scale(0.99);
}

.oh-card:hover {
  box-shadow: 0 6px 18px rgba(25, 28, 30, 0.08);
}

/* Table (QR) mode never had a way into the order detail on the Completed
   Orders screen — don't pretend. */
.oh-card--static {
  cursor: default;
}

.oh-card--static:active {
  transform: none;
}

.oh-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.oh-card__field {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.oh-card__field--right {
  align-items: flex-end;
  text-align: right;
  flex: none;
}

.oh-card__label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.55);
}

.oh-card__label--sub {
  margin-top: 0.55rem;
}

.oh-card__label--mt {
  margin-top: 0.75rem;
}

.oh-card__value {
  font-weight: 800;
  font-size: 1.05rem;
  word-break: break-all;
}

.oh-card__value--sub {
  font-size: 0.85rem;
}

.oh-card__date {
  margin-top: 0.55rem;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
}

.oh-card__table-badge {
  font-weight: 800;
  font-size: 1.05rem;
}

.oh-card__status {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 700;
  font-size: 0.9rem;
}

.oh-card__status--paid,
.oh-card__status--completed {
  color: #1f8a4c;
}

.oh-card__status--unpaid {
  color: #1a1c1a;
}

.oh-card__status--voided {
  color: #c62828;
}

.oh-card__status--confirming {
  color: #1565c0;
}

.oh-card__status--preparing {
  color: #c77700;
}

.oh-card__status--seated {
  color: #546e7a;
}

.oh-card__status--ready {
  color: #00897b;
}

.oh-card__pulse {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.oh-card__pulse--paid,
.oh-card__pulse--completed {
  background: #1f8a4c;
}

.oh-card__pulse--unpaid {
  background: #1a1c1a;
  animation: oh-order-pulse 1.4s ease-in-out infinite;
}

.oh-card__pulse--voided {
  background: #c62828;
}

.oh-card__pulse--confirming {
  background: #1565c0;
  animation: oh-order-pulse 1.4s ease-in-out infinite;
}

.oh-card__pulse--preparing {
  background: #c77700;
  animation: oh-order-pulse 1.4s ease-in-out infinite;
}

.oh-card__pulse--seated {
  background: #546e7a;
  animation: oh-order-pulse 1.4s ease-in-out infinite;
}

.oh-card__pulse--ready {
  background: #00897b;
  animation: oh-order-pulse 1.4s ease-in-out infinite;
}

@keyframes oh-order-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.85);
  }
}

.oh-card__items {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.oh-card__entry {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.oh-card__modifiers {
  margin: 0;
  padding: 0 0 0 0.5rem;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.oh-card__mod-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
}

.oh-card__mod-line--header {
  margin-top: 0.15rem;
  font-weight: 700;
  color: #201a1a;
}

.oh-card__mod-line--nested {
  padding-left: 0.5rem;
}

.oh-card__mod-name {
  flex: 1;
  min-width: 0;
}

/* Sold-out modifier — greyed with a themed "Not Available" note. */
.oh-card__mod-name--off {
  opacity: 0.3;
}

.oh-card__mod-na {
  flex: 0 0 auto;
  font-size: 12px;
  color: var(--color);
}

.oh-card__mod-price {
  white-space: nowrap;
}

.oh-card__row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  font-size: 0.9rem;
}

.oh-card__row-left {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  min-width: 0;
}

.oh-card__qty {
  font-weight: 700;
  color: rgba(0, 0, 0, 0.55);
  flex: 0 0 auto;
}

.oh-card__name {
  font-weight: 500;
  word-break: break-word;
}

.oh-card__price {
  font-weight: 700;
  flex: 0 0 auto;
}

.oh-card__price-stack {
  display: grid;
  justify-items: end;
  flex: 0 0 auto;
}

.oh-card__price-was {
  font-size: 0.75rem;
  font-weight: 400;
  text-decoration: line-through;
  color: rgba(0, 0, 0, 0.4);
}

.oh-card__disclaimer {
  margin: 0;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 10px;
  font-style: italic;
  color: rgba(0, 0, 0, 0.55);
}

.oh-card__summary {
  margin-top: 0.25rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.oh-card__summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
}

.oh-card__summary-row--discount {
  color: var(--rw);
  font-weight: 600;
}

.oh-card__summary-row--bill-total {
  font-weight: 800;
  color: #1a1c1a;
}

.oh-card__summary-row--total {
  margin-top: 0.35rem;
  padding-top: 0.55rem;
  border-top: 1px dashed rgba(0, 0, 0, 0.12);
  font-size: 1rem;
  font-weight: 800;
  color: #1a1c1a;
}

.oh-card__summary-amount--payable {
  color: var(--rw);
}

.oh-load-more {
  align-self: center;
  margin-top: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  color: #1a1c1a;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.2s ease;
}

.oh-load-more:not(:disabled):active {
  transform: scale(0.98);
}

.oh-empty {
  margin-top: 2rem;
  padding: 2rem 1.5rem;
  background: #fff;
  border-radius: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 14px rgba(25, 28, 30, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;
}

.oh-empty__icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--rw-18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--rw);
}

.oh-empty__icon .material-icons-round {
  font-size: 32px;
}

.oh-empty__title {
  margin: 0;
  font-family: inherit;
  font-weight: 800;
  font-size: 1.15rem;
  color: #1a1c1a;
}

.oh-empty__text {
  margin: 0;
  font-size: 0.9rem;
  color: var(--pf-muted);
}

.oh-empty__btn {
  margin-top: 0.5rem;
  padding: 0.75rem 1.75rem;
  border-radius: 999px;
  border: none;
  background: var(--pf);
  color: #ffffff;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.oh-empty__btn:active {
  transform: scale(0.98);
}

.oh-snackbar {
  position: fixed;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 60;
}

footer {
  background-color: var(--theme-30, white);
  padding-top: 10px;
}
</style>
